import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Box,
  Building2,
  Globe,
  Calculator,
  Users,
  Inbox,
  Plus,
  ChevronLeft,
  Star,
  Trash2,
  Server,
  Layers,
  Save,
  EyeOff,
  Eye,
  ChevronDown,
  ChevronRight,
  Code,
  Copy,
  RefreshCw,
  Key,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import api from "../lib/api";
import { cn } from "../lib/utils";
import { useAuth } from "../context/AuthContext";

const PRODUCT_ICON_MAP = { Building2, Globe, Calculator, Users, Inbox, Box };
const ICON_OPTIONS = ["Building2", "Globe", "Calculator", "Users", "Inbox", "Box"];
const COLOR_OPTIONS = ["orange", "amber", "yellow", "blue", "emerald", "violet", "zinc"];

const PRODUCT_COLOR_CLASSES = {
  orange: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  yellow: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  blue: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  violet: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  zinc: "bg-zinc-700/40 text-zinc-300 border-zinc-600/40",
};
const productClass = (color) => PRODUCT_COLOR_CLASSES[color] || PRODUCT_COLOR_CLASSES.zinc;

const slugify = (raw) => {
  if (!raw) return "";
  return String(raw)
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

const localId = (prefix = "tmp") => `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

const emptyMirror = () => ({
  _localId: localId("mir"),
  slug: "",
  label: "",
  url: "",
  notes: "",
  is_active: true,
});

const emptyInstance = () => ({
  _localId: localId("inst"),
  slug: "",
  label: "",
  url: "",
  notes: "",
  is_default: false,
  is_active: true,
  mirrors: [],
});

// Format a "last seen" relative timestamp for embed widget heartbeats.
// Returns: "Active — 2 min ago" / "Silent — 4 days ago" / "Never seen".
// Used on each deployment row so admin sees at a glance whether the
// customer's site has the widget actually installed and routing traffic.
function formatEmbedLastSeen(iso) {
  if (!iso) return { text: "Never seen", tone: "gray" };
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return { text: "Never seen", tone: "gray" };
  const ageMs = Date.now() - ts;
  const min = ageMs / 60000;
  const hr = min / 60;
  const day = hr / 24;
  let text;
  if (min < 1) text = "Active — just now";
  else if (min < 60) text = `Active — ${Math.floor(min)} min ago`;
  else if (hr < 24) text = `Active — ${Math.floor(hr)} h ago`;
  else if (day < 30) text = `Last seen ${Math.floor(day)} d ago`;
  else text = `Last seen ${new Date(iso).toLocaleDateString()}`;
  // Tone: < 1h = green (live), < 24h = amber, > 24h = red (probably gone).
  const tone = min < 60 ? "green" : hr < 24 ? "amber" : "red";
  return { text, tone };
}

const SupportProductDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingBasic, setSavingBasic] = useState(false);
  const [savingDeployments, setSavingDeployments] = useState(false);
  const [expandedDeployment, setExpandedDeployment] = useState(null);

  // Local form state, separate from server state so we can dirty-track.
  const [basic, setBasic] = useState({
    name: "",
    short_label: "",
    description: "",
    url: "",
    icon: "Box",
    color: "zinc",
    is_default: false,
    is_active: true,
    sort_order: 50,
    context: "",
  });
  const [instances, setInstances] = useState([]);
  const [partnerMode, setPartnerMode] = useState({
    partner_default: false,
    require_instance_for_partners: false,
    require_mirror_for_partners: false,
    partner_persona: "",
  });
  const [savingPartner, setSavingPartner] = useState(false);

  // Embed widget — per-instance API key minting. The freshly minted key is
  // only ever returned once, so we capture it into a one-shot dialog state
  // that the admin can copy-paste before navigating away.
  const [embedDialog, setEmbedDialog] = useState(null); // {instance_slug, api_key, snippet, allowed_origins}
  const [embedBusy, setEmbedBusy] = useState(false);
  const [editingOriginsInst, setEditingOriginsInst] = useState(null); // _localId
  const [originDraft, setOriginDraft] = useState("");

  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/support-products/${slug}`);
      setProduct(data);
      setBasic({
        name: data.name || "",
        short_label: data.short_label || "",
        description: data.description || "",
        url: data.url || "",
        icon: data.icon || "Box",
        color: data.color || "zinc",
        is_default: !!data.is_default,
        is_active: data.is_active !== false,
        sort_order: data.sort_order ?? 50,
        context: data.context || "",
      });
      setPartnerMode({
        partner_default: !!data.partner_default,
        require_instance_for_partners: Array.isArray(data.require_instance_for_roles)
          && data.require_instance_for_roles.includes("partner"),
        require_mirror_for_partners: Array.isArray(data.require_mirror_for_roles)
          && data.require_mirror_for_roles.includes("partner"),
        partner_persona: data.partner_persona || "",
      });
      setInstances(
        Array.isArray(data.instances)
          ? data.instances.map((i) => ({
              _localId: i.id || localId("inst"),
              id: i.id,
              slug: i.slug || "",
              label: i.label || "",
              url: i.url || "",
              notes: i.notes || "",
              is_default: !!i.is_default,
              is_active: i.is_active !== false,
              // Embed widget config (per-instance API key + origin allowlist).
              // Hash + preview round-trip from server but the full key is only
              // ever returned once — at rotate-key time. We just display the
              // preview here.
              embed_enabled: !!i.embed_enabled,
              embed_api_key_preview: i.embed_api_key_preview || null,
              embed_allowed_origins: Array.isArray(i.embed_allowed_origins) ? i.embed_allowed_origins : [],
              embed_key_created_at: i.embed_key_created_at || null,
              embed_last_seen_at: i.embed_last_seen_at || null,
              embed_message_count: i.embed_message_count || 0,
              embed_ping_count: i.embed_ping_count || 0,
              mirrors: Array.isArray(i.mirrors)
                ? i.mirrors.map((m) => ({
                    _localId: m.id || localId("mir"),
                    id: m.id,
                    slug: m.slug || "",
                    label: m.label || "",
                    url: m.url || "",
                    notes: m.notes || "",
                    is_active: m.is_active !== false,
                  }))
                : [],
            }))
          : []
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Product not found");
      navigate("/settings/support-products");
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  useEffect(() => { fetchProduct(); }, [fetchProduct]);

  const saveBasic = async () => {
    if (!basic.name.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      setSavingBasic(true);
      await api.put(`/support-products/${slug}`, {
        name: basic.name.trim(),
        short_label: basic.short_label.trim() || basic.name.trim(),
        description: basic.description,
        url: basic.url,
        icon: basic.icon,
        color: basic.color,
        is_default: basic.is_default,
        is_active: basic.is_active,
        context: basic.context,
        sort_order: Number(basic.sort_order) || 50,
      });
      toast.success("Saved");
      fetchProduct();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSavingBasic(false);
    }
  };

  const savePartnerMode = async () => {
    try {
      setSavingPartner(true);
      // Convert toggles back to role-array shape the backend expects.
      const requireInstance = partnerMode.require_instance_for_partners ? ["partner"] : [];
      const requireMirror = partnerMode.require_mirror_for_partners ? ["partner"] : [];
      await api.put(`/support-products/${slug}`, {
        partner_default: !!partnerMode.partner_default,
        require_instance_for_roles: requireInstance,
        require_mirror_for_roles: requireMirror,
        partner_persona: partnerMode.partner_persona || "",
      });
      toast.success("Partner mode saved");
      fetchProduct();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save partner mode");
    } finally {
      setSavingPartner(false);
    }
  };

  const saveDeployments = async () => {
    try {
      setSavingDeployments(true);
      const cleaned = instances
        .map((inst) => ({
          id: inst.id,
          slug: (inst.slug || slugify(inst.label || inst.url) || "").toLowerCase(),
          label: (inst.label || "").trim(),
          url: (inst.url || "").trim(),
          notes: (inst.notes || "").trim(),
          is_default: !!inst.is_default,
          is_active: inst.is_active !== false,
          mirrors: (inst.mirrors || [])
            .map((m) => ({
              id: m.id,
              slug: (m.slug || slugify(m.label || m.url) || "").toLowerCase(),
              label: (m.label || "").trim(),
              url: (m.url || "").trim(),
              notes: (m.notes || "").trim(),
              is_active: m.is_active !== false,
            }))
            .filter((m) => m.slug || m.label || m.url),
        }))
        .filter((inst) => inst.slug || inst.label || inst.url);
      await api.put(`/support-products/${slug}`, { instances: cleaned });
      toast.success("Deployments saved");
      fetchProduct();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save deployments");
    } finally {
      setSavingDeployments(false);
    }
  };

  const updateInstance = (lid, patch) => {
    setInstances((s) => s.map((i) => (i._localId === lid ? { ...i, ...patch } : i)));
  };

  const promoteDefaultInstance = (lid) => {
    setInstances((s) => s.map((i) => ({ ...i, is_default: i._localId === lid })));
  };

  const removeInstance = (lid) => {
    setInstances((s) => s.filter((i) => i._localId !== lid));
    if (expandedDeployment === lid) setExpandedDeployment(null);
  };

  const addInstance = () => {
    const fresh = emptyInstance();
    setInstances((s) => [...s, fresh]);
    setExpandedDeployment(fresh._localId);
  };

  // ----- Embed widget management ----------------------------------------

  const rotateEmbedKey = async (inst) => {
    if (!inst.slug) {
      toast.error("Save the deployment first (slug is required) before minting a key.");
      return;
    }
    if (inst.embed_api_key_preview) {
      const ok = window.confirm(
        "This deployment already has an embed key. Rotating will INVALIDATE the existing snippet on the customer's site immediately. Continue?"
      );
      if (!ok) return;
    }
    try {
      setEmbedBusy(true);
      const { data } = await api.post(
        `/support-products/${slug}/instances/${inst.slug}/embed/rotate-key`,
        {},
      );
      setEmbedDialog({
        instance_slug: inst.slug,
        instance_label: inst.label,
        api_key: data.api_key,
        snippet: data.snippet,
        allowed_origins: data.allowed_origins || [],
      });
      toast.success("Embed key minted — copy it now (this is the only time you'll see it).");
      fetchProduct();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mint embed key");
    } finally {
      setEmbedBusy(false);
    }
  };

  const revokeEmbedKey = async (inst) => {
    if (!inst.slug) return;
    const ok = window.confirm(
      `Revoke the embed key for "${inst.label || inst.slug}"? The widget on the customer's site will stop working until you mint a new key and re-paste the snippet.`
    );
    if (!ok) return;
    try {
      setEmbedBusy(true);
      await api.post(
        `/support-products/${slug}/instances/${inst.slug}/embed/revoke-key`,
      );
      toast.success("Embed key revoked");
      fetchProduct();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to revoke embed key");
    } finally {
      setEmbedBusy(false);
    }
  };

  const saveEmbedOrigins = async (inst, origins) => {
    if (!inst.slug) return;
    try {
      setEmbedBusy(true);
      await api.put(
        `/support-products/${slug}/instances/${inst.slug}/embed/origins`,
        { allowed_origins: origins },
      );
      toast.success("Allowed origins saved");
      setEditingOriginsInst(null);
      setOriginDraft("");
      fetchProduct();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save origins");
    } finally {
      setEmbedBusy(false);
    }
  };

  const copyToClipboard = async (text, label = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch (e) {
      toast.error("Couldn't copy to clipboard");
    }
  };

  const updateMirror = (instLid, mirLid, patch) => {
    setInstances((s) => s.map((inst) => {
      if (inst._localId !== instLid) return inst;
      return {
        ...inst,
        mirrors: inst.mirrors.map((m) => (m._localId === mirLid ? { ...m, ...patch } : m)),
      };
    }));
  };

  const removeMirror = (instLid, mirLid) => {
    setInstances((s) => s.map((inst) => {
      if (inst._localId !== instLid) return inst;
      return { ...inst, mirrors: inst.mirrors.filter((m) => m._localId !== mirLid) };
    }));
  };

  const addMirror = (instLid) => {
    setInstances((s) => s.map((inst) => {
      if (inst._localId !== instLid) return inst;
      return { ...inst, mirrors: [...(inst.mirrors || []), emptyMirror()] };
    }));
  };

  if (loading) {
    return <div className="p-8 text-zinc-400 text-sm">Loading…</div>;
  }
  if (!product) return null;

  const Icon = PRODUCT_ICON_MAP[basic.icon] || Box;

  return (
    <div className="space-y-5 p-6 max-w-5xl" data-testid="support-product-detail">
      {/* Top bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="ghost"
          size="sm"
          className="text-zinc-400 hover:text-zinc-100"
          onClick={() => navigate("/settings/support-products")}
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Products
        </Button>
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center border", productClass(basic.color))}>
          <Icon className="w-5 h-5" />
        </div>
        <h1 className="text-xl font-bold text-zinc-50">{basic.name || product.slug}</h1>
        <span className="text-xs text-zinc-500 font-mono">{product.slug}</span>
        {basic.is_default && (
          <Badge variant="outline" className="text-[10px] border-orange-500/40 text-orange-300">
            <Star className="w-2.5 h-2.5 mr-0.5" /> default
          </Badge>
        )}
        {!basic.is_active && (
          <Badge variant="outline" className="text-[10px] border-zinc-600 text-zinc-500">inactive</Badge>
        )}
      </div>

      {/* Non-admin (PM/dev) banner — quick hint that they have edit
          access only on Deployments. Admin sees nothing here. */}
      {!isAdmin && (
        <Card className="bg-blue-950/30 border-blue-700/40">
          <CardContent className="p-3 text-xs text-blue-200">
            You can manage <strong>deployments and mirrors</strong> for this
            product. Other product settings (name, AI context, partner mode)
            are admin-only — ask an admin if you need them changed.
          </CardContent>
        </Card>
      )}

      {/* Section: Basic info — admin only */}
      {isAdmin && (
      <Card className="bg-zinc-900/40 border-zinc-800">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-zinc-200">Basic info</h2>
            <Button size="sm" onClick={saveBasic} disabled={savingBasic} className="bg-orange-600 hover:bg-orange-700 h-8">
              <Save className="w-3.5 h-3.5 mr-1" />
              {savingBasic ? "Saving…" : "Save basic"}
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-zinc-500 uppercase">Display name</label>
              <Input
                value={basic.name}
                onChange={(e) => setBasic((s) => ({ ...s, name: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1"
              />
            </div>
            <div>
              <label className="text-[11px] text-zinc-500 uppercase">Short label</label>
              <Input
                value={basic.short_label}
                onChange={(e) => setBasic((s) => ({ ...s, short_label: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-zinc-500 uppercase">Description</label>
              <Input
                value={basic.description}
                onChange={(e) => setBasic((s) => ({ ...s, description: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1"
              />
            </div>
            <div>
              <label className="text-[11px] text-zinc-500 uppercase">Canonical URL</label>
              <Input
                value={basic.url}
                onChange={(e) => setBasic((s) => ({ ...s, url: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1 font-mono"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[11px] text-zinc-500 uppercase">Icon</label>
              <Select value={basic.icon} onValueChange={(v) => setBasic((s) => ({ ...s, icon: v }))}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {ICON_OPTIONS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] text-zinc-500 uppercase">Color</label>
              <Select value={basic.color} onValueChange={(v) => setBasic((s) => ({ ...s, color: v }))}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {COLOR_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c}>
                      <span className={cn("inline-block w-3 h-3 rounded mr-2 border", productClass(c))} />
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] text-zinc-500 uppercase">Sort order</label>
              <Input
                type="number"
                value={basic.sort_order}
                onChange={(e) => setBasic((s) => ({ ...s, sort_order: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1"
              />
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-1.5 text-xs text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                checked={basic.is_default}
                onChange={(e) => setBasic((s) => ({ ...s, is_default: e.target.checked }))}
                className="accent-orange-500"
              />
              Default for new chats
            </label>
            <label className="flex items-center gap-1.5 text-xs text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                checked={basic.is_active}
                onChange={(e) => setBasic((s) => ({ ...s, is_active: e.target.checked }))}
                className="accent-emerald-500"
              />
              Active
            </label>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Section: AI context — admin only */}
      {isAdmin && (
      <Card className="bg-zinc-900/40 border-zinc-800">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-zinc-200">AI context</h2>
            <Button size="sm" onClick={saveBasic} disabled={savingBasic} className="bg-orange-600 hover:bg-orange-700 h-8" title="Saves AI context together with basic info">
              <Save className="w-3.5 h-3.5 mr-1" /> Save
            </Button>
          </div>
          <p className="text-[11px] text-zinc-500">
            Inlined into the AI system prompt whenever a chat is scoped to this product. Tell the model what
            this product IS, what users will ask, what to NOT confuse it with.
          </p>
          <Textarea
            value={basic.context}
            onChange={(e) => setBasic((s) => ({ ...s, context: e.target.value }))}
            rows={6}
            className="bg-zinc-800 border-zinc-700 text-zinc-100 text-sm font-mono"
          />
        </CardContent>
      </Card>
      )}

      {/* Section: Partner mode — admin only */}
      {isAdmin && (
      <Card className="bg-zinc-900/40 border-zinc-800">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h2 className="text-sm font-semibold text-zinc-200 flex items-center gap-1.5">
                <Users className="w-4 h-4" /> Partner mode
              </h2>
              <p className="text-[11px] text-zinc-500 mt-0.5">
                For TRP partners: stricter chat that mandates deployment+mirror picks
                and runs an AI triage gatekeeper that auto-files only the worthwhile
                feedback to the dev backlog. Calming-gatekeeper persona is added to the
                AI system prompt.
              </p>
            </div>
            <Button
              size="sm"
              onClick={savePartnerMode}
              disabled={savingPartner}
              className="bg-orange-600 hover:bg-orange-700 h-8"
            >
              <Save className="w-3.5 h-3.5 mr-1" />
              {savingPartner ? "Saving…" : "Save partner mode"}
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="flex items-start gap-2 p-2.5 rounded border border-zinc-800 bg-zinc-950/30 cursor-pointer hover:border-zinc-700">
              <input
                type="checkbox"
                checked={partnerMode.partner_default}
                onChange={(e) => setPartnerMode((s) => ({ ...s, partner_default: e.target.checked }))}
                className="accent-orange-500 mt-0.5"
              />
              <div>
                <div className="text-xs text-zinc-200">Default for partners</div>
                <p className="text-[10px] text-zinc-500 mt-0.5">
                  Vexos is the typical pick — partners' widget opens here
                  instead of HUB.
                </p>
              </div>
            </label>
            <label className="flex items-start gap-2 p-2.5 rounded border border-zinc-800 bg-zinc-950/30 cursor-pointer hover:border-zinc-700">
              <input
                type="checkbox"
                checked={partnerMode.require_instance_for_partners}
                onChange={(e) => setPartnerMode((s) => ({ ...s, require_instance_for_partners: e.target.checked }))}
                className="accent-orange-500 mt-0.5"
              />
              <div>
                <div className="text-xs text-zinc-200">Require deployment</div>
                <p className="text-[10px] text-zinc-500 mt-0.5">
                  Partner cannot send a message until they pick which deployment.
                  Hides the "Platform-wide" escape.
                </p>
              </div>
            </label>
            <label className="flex items-start gap-2 p-2.5 rounded border border-zinc-800 bg-zinc-950/30 cursor-pointer hover:border-zinc-700">
              <input
                type="checkbox"
                checked={partnerMode.require_mirror_for_partners}
                onChange={(e) => setPartnerMode((s) => ({ ...s, require_mirror_for_partners: e.target.checked }))}
                className="accent-orange-500 mt-0.5"
              />
              <div>
                <div className="text-xs text-zinc-200">Require mirror</div>
                <p className="text-[10px] text-zinc-500 mt-0.5">
                  When deployment has 2+ mirrors, partner must pick which URL
                  (production / staging / …). Stops prod ↔ staging confusion.
                </p>
              </div>
            </label>
          </div>
          <div>
            <label className="text-[11px] text-zinc-500 uppercase">Partner persona (extra system-prompt block)</label>
            <Textarea
              value={partnerMode.partner_persona}
              onChange={(e) => setPartnerMode((s) => ({ ...s, partner_persona: e.target.value }))}
              rows={5}
              className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1 text-xs font-mono"
              placeholder="Tell the AI how to talk to partners on this product. Tone, gatekeeping rules, what NOT to promise…"
            />
            <p className="text-[10px] text-zinc-500 mt-1">
              Appended to the base "calming gatekeeper" persona only when
              role=partner. Empty = use base persona unchanged.
            </p>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Section: Deployments + mirrors */}
      <Card className="bg-zinc-900/40 border-zinc-800">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-sm font-semibold text-zinc-200 flex items-center gap-1.5">
                <Server className="w-4 h-4" /> Deployments
                <span className="text-xs text-zinc-500 font-normal">
                  ({instances.length} total · {instances.filter((i) => i.is_active).length} active)
                </span>
              </h2>
              <p className="text-[11px] text-zinc-500 mt-0.5">
                Each deployment = one customer subdomain (e.g. <span className="font-mono text-zinc-400">ancora.vexos.app</span>).
                Add mirrors for prod/staging/alt-domains — chat picker steps through deployment then mirror.
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="border-zinc-700 h-8" onClick={addInstance} data-testid="deployment-add">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add deployment
              </Button>
              <Button
                size="sm"
                onClick={saveDeployments}
                disabled={savingDeployments}
                className="bg-emerald-600 hover:bg-emerald-700 h-8"
                data-testid="deployments-save"
              >
                <Save className="w-3.5 h-3.5 mr-1" />
                {savingDeployments ? "Saving…" : "Save deployments"}
              </Button>
            </div>
          </div>

          {instances.length === 0 ? (
            <p className="text-sm text-zinc-500 italic text-center py-6">
              No deployments. Single-tenant — chat picker stays one step.
            </p>
          ) : (
            <div className="space-y-2">
              {instances.map((inst) => {
                const expanded = expandedDeployment === inst._localId;
                const activeMirrors = (inst.mirrors || []).filter((m) => m.is_active).length;
                return (
                  <div
                    key={inst._localId}
                    className={cn(
                      "rounded-md border bg-zinc-950/40",
                      inst.is_default ? "border-orange-500/40" : "border-zinc-800",
                      !inst.is_active && "opacity-60"
                    )}
                  >
                    {/* Row header */}
                    <div className="grid grid-cols-12 gap-2 p-2.5 items-start">
                      <button
                        type="button"
                        onClick={() => setExpandedDeployment(expanded ? null : inst._localId)}
                        className="col-span-1 flex items-center justify-center text-zinc-500 hover:text-zinc-100"
                        title={expanded ? "Collapse" : "Expand"}
                      >
                        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                      <Input
                        placeholder="Label (e.g. Ancora)"
                        value={inst.label}
                        onChange={(e) => updateInstance(inst._localId, { label: e.target.value })}
                        className="col-span-3 h-8 bg-zinc-900 border-zinc-700 text-xs text-zinc-100"
                      />
                      <Input
                        placeholder="Primary URL"
                        value={inst.url}
                        onChange={(e) => updateInstance(inst._localId, { url: e.target.value })}
                        className="col-span-4 h-8 bg-zinc-900 border-zinc-700 text-xs text-zinc-100 font-mono"
                      />
                      <Input
                        placeholder="slug"
                        value={inst.slug}
                        onChange={(e) => updateInstance(inst._localId, { slug: e.target.value })}
                        className="col-span-2 h-8 bg-zinc-900 border-zinc-700 text-xs text-zinc-100 font-mono"
                        title="Auto-derived from label/URL if blank"
                      />
                      <div className="col-span-2 flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => promoteDefaultInstance(inst._localId)}
                          className={cn(
                            "p-1 rounded hover:bg-zinc-800",
                            inst.is_default ? "text-orange-300" : "text-zinc-500 hover:text-zinc-200"
                          )}
                          title={inst.is_default ? "Default deployment" : "Make default"}
                        >
                          <Star className={cn("w-3.5 h-3.5", inst.is_default && "fill-orange-400")} />
                        </button>
                        <button
                          type="button"
                          onClick={() => updateInstance(inst._localId, { is_active: !inst.is_active })}
                          className="p-1 rounded hover:bg-zinc-800 text-zinc-500 hover:text-zinc-200"
                          title={inst.is_active ? "Deactivate" : "Activate"}
                        >
                          {inst.is_active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                        </button>
                        <button
                          type="button"
                          onClick={() => removeInstance(inst._localId)}
                          className="p-1 rounded hover:bg-red-500/20 text-zinc-500 hover:text-red-300"
                          title="Remove deployment"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Inline meta — mirror count chip */}
                    {!expanded && activeMirrors > 0 && (
                      <div className="px-3 pb-2 -mt-1 flex items-center gap-1.5 text-[10px] text-zinc-500">
                        <Layers className="w-3 h-3" />
                        {activeMirrors} mirror(s)
                      </div>
                    )}

                    {/* Expanded body — notes + mirrors editor */}
                    {expanded && (
                      <div className="px-3 pb-3 pt-1 border-t border-zinc-800/60 space-y-2">
                        <div>
                          <label className="text-[10px] text-zinc-500 uppercase">Notes (AI sees this)</label>
                          <Input
                            placeholder="e.g. live since 2026-04, Ukrainian language, custom payment flow"
                            value={inst.notes}
                            onChange={(e) => updateInstance(inst._localId, { notes: e.target.value })}
                            className="h-8 bg-zinc-900 border-zinc-700 text-xs text-zinc-100 mt-0.5"
                          />
                        </div>

                        <div className="border-t border-zinc-800/60 pt-2">
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="text-[10px] text-zinc-400 uppercase flex items-center gap-1.5">
                              <Layers className="w-3 h-3" /> Mirrors / alternate URLs
                            </label>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-[11px] border-zinc-700"
                              onClick={() => addMirror(inst._localId)}
                            >
                              <Plus className="w-3 h-3 mr-0.5" /> Add mirror
                            </Button>
                          </div>
                          {(!inst.mirrors || inst.mirrors.length === 0) ? (
                            <p className="text-[10px] text-zinc-600 italic">
                              No mirrors. Picker skips the mirror step for this deployment.
                            </p>
                          ) : (
                            <div className="space-y-1.5">
                              {inst.mirrors.map((m) => (
                                <div
                                  key={m._localId}
                                  className={cn(
                                    "rounded border bg-zinc-900/60 p-2",
                                    m.is_active ? "border-zinc-700" : "border-zinc-800 opacity-60"
                                  )}
                                >
                                  <div className="grid grid-cols-12 gap-1.5">
                                    <Input
                                      placeholder="Label (e.g. production / staging / alt-domain)"
                                      value={m.label}
                                      onChange={(e) => updateMirror(inst._localId, m._localId, { label: e.target.value })}
                                      className="col-span-4 h-7 bg-zinc-900 border-zinc-700 text-[11px] text-zinc-100"
                                    />
                                    <Input
                                      placeholder="https://…"
                                      value={m.url}
                                      onChange={(e) => updateMirror(inst._localId, m._localId, { url: e.target.value })}
                                      className="col-span-5 h-7 bg-zinc-900 border-zinc-700 text-[11px] text-zinc-100 font-mono"
                                    />
                                    <Input
                                      placeholder="slug"
                                      value={m.slug}
                                      onChange={(e) => updateMirror(inst._localId, m._localId, { slug: e.target.value })}
                                      className="col-span-2 h-7 bg-zinc-900 border-zinc-700 text-[11px] text-zinc-100 font-mono"
                                    />
                                    <div className="col-span-1 flex items-center justify-end gap-0.5">
                                      <button
                                        type="button"
                                        onClick={() => updateMirror(inst._localId, m._localId, { is_active: !m.is_active })}
                                        className="p-1 rounded hover:bg-zinc-800 text-zinc-500 hover:text-zinc-200"
                                        title={m.is_active ? "Deactivate" : "Activate"}
                                      >
                                        {m.is_active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => removeMirror(inst._localId, m._localId)}
                                        className="p-1 rounded hover:bg-red-500/20 text-zinc-500 hover:text-red-300"
                                        title="Remove mirror"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>
                                  <Input
                                    placeholder="Mirror notes (optional, AI sees this)"
                                    value={m.notes}
                                    onChange={(e) => updateMirror(inst._localId, m._localId, { notes: e.target.value })}
                                    className="h-7 bg-zinc-900 border-zinc-700 text-[11px] text-zinc-100 mt-1.5"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Embed widget — public chat snippet for the customer's site. */}
                        <div className="border-t border-zinc-800/60 pt-2">
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="text-[10px] text-zinc-400 uppercase flex items-center gap-1.5">
                              <Code className="w-3 h-3" /> Embed widget
                              {inst.embed_enabled && inst.embed_api_key_preview ? (
                                <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[9px] uppercase ml-1">enabled</Badge>
                              ) : (
                                <Badge className="bg-zinc-700/40 text-zinc-400 border-zinc-600/40 text-[9px] uppercase ml-1">off</Badge>
                              )}
                            </label>
                            <div className="flex items-center gap-1">
                              {inst.embed_enabled && inst.embed_api_key_preview ? (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-[11px] border-zinc-700"
                                    onClick={() => rotateEmbedKey(inst)}
                                    disabled={embedBusy}
                                    title="Rotate the API key (invalidates the existing snippet)"
                                  >
                                    <RefreshCw className="w-3 h-3 mr-0.5" /> Rotate key
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-[11px] border-red-700/40 text-red-300 hover:bg-red-500/10"
                                    onClick={() => revokeEmbedKey(inst)}
                                    disabled={embedBusy}
                                  >
                                    <Trash2 className="w-3 h-3 mr-0.5" /> Revoke
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-[11px] border-orange-500/40 text-orange-300 hover:bg-orange-500/10"
                                  onClick={() => rotateEmbedKey(inst)}
                                  disabled={embedBusy || !inst.slug}
                                  title={!inst.slug ? "Save the deployment first (slug required)" : "Mint a fresh embed API key"}
                                >
                                  <Key className="w-3 h-3 mr-0.5" /> Generate key
                                </Button>
                              )}
                            </div>
                          </div>
                          {inst.embed_enabled && inst.embed_api_key_preview ? (
                            <div className="space-y-1.5 text-[11px]">
                              {/* Live status — heartbeat from /embed/config + /embed/send.
                                  Tells admin at a glance whether the widget is actually
                                  installed and routing traffic on the customer's site. */}
                              {(() => {
                                const live = formatEmbedLastSeen(inst.embed_last_seen_at);
                                const toneCls = live.tone === "green"
                                  ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                                  : live.tone === "amber"
                                    ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
                                    : live.tone === "red"
                                      ? "bg-red-500/15 text-red-300 border-red-500/40"
                                      : "bg-zinc-700/40 text-zinc-400 border-zinc-600/40";
                                const dot = live.tone === "green"
                                  ? "bg-emerald-400 animate-pulse"
                                  : live.tone === "amber" ? "bg-amber-400"
                                  : live.tone === "red" ? "bg-red-400" : "bg-zinc-500";
                                return (
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className={cn("inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-[10px]", toneCls)}>
                                      <span className={cn("w-1.5 h-1.5 rounded-full", dot)} />
                                      {live.text}
                                    </span>
                                    {(inst.embed_message_count || 0) > 0 && (
                                      <span className="text-[10px] text-zinc-500">
                                        {inst.embed_message_count} message{inst.embed_message_count === 1 ? "" : "s"} routed
                                      </span>
                                    )}
                                    {(inst.embed_ping_count || 0) > 0 && (
                                      <span className="text-[10px] text-zinc-600">
                                        · {inst.embed_ping_count} widget load{inst.embed_ping_count === 1 ? "" : "s"}
                                      </span>
                                    )}
                                  </div>
                                );
                              })()}
                              {/* Ready-to-paste snippet — copy-button + clear how-to. */}
                              {(() => {
                                const portalOrigin = typeof window !== "undefined"
                                  ? window.location.origin : "";
                                const snippet = `<!-- Smartorange Support Widget -->\n<script async\n  src="${portalOrigin}/embed/support-widget.js"\n  data-api-key="${inst.embed_api_key_preview}…"></script>`;
                                return (
                                  <div className="rounded border border-zinc-700 bg-zinc-950/40 p-2">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-[10px] text-zinc-500 uppercase tracking-wide">
                                        Snippet to paste before {`</body>`}
                                      </span>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-6 text-[10px] border-zinc-700"
                                        onClick={() => copyToClipboard(snippet, "Snippet preview copied (full key needed — rotate to get it)")}
                                        title="Note: this is just the preview key prefix. Use 'Rotate key' to get a fresh full key."
                                      >
                                        <Copy className="w-3 h-3 mr-0.5" /> Copy preview
                                      </Button>
                                    </div>
                                    <pre className="text-[10px] font-mono text-zinc-400 whitespace-pre-wrap break-all">{snippet}</pre>
                                    <p className="text-[10px] text-zinc-500 mt-1">
                                      The preview only shows the first chars of the key. Click <strong>Rotate key</strong> to mint a fresh
                                      full key — that's the only time it's shown in plaintext, so copy it then.
                                    </p>
                                  </div>
                                );
                              })()}
                              <div className="flex items-center gap-2 text-zinc-500">
                                <span className="font-mono text-zinc-300">{inst.embed_api_key_preview}</span>
                                <span>·</span>
                                <span>created {inst.embed_key_created_at ? new Date(inst.embed_key_created_at).toLocaleDateString() : "—"}</span>
                              </div>
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-[10px] text-zinc-500 uppercase">Allowed origins ({(inst.embed_allowed_origins || []).length})</span>
                                  {editingOriginsInst === inst._localId ? (
                                    <div className="flex items-center gap-1">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-6 text-[10px] border-zinc-700"
                                        onClick={() => { setEditingOriginsInst(null); setOriginDraft(""); }}
                                      >Cancel</Button>
                                      <Button
                                        size="sm"
                                        className="h-6 text-[10px]"
                                        onClick={() => {
                                          const list = originDraft.split(/[\s,]+/).map((o) => o.trim()).filter(Boolean);
                                          saveEmbedOrigins(inst, list);
                                        }}
                                      >Save</Button>
                                    </div>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-6 text-[10px] border-zinc-700"
                                      onClick={() => {
                                        setEditingOriginsInst(inst._localId);
                                        setOriginDraft((inst.embed_allowed_origins || []).join("\n"));
                                      }}
                                    >Edit</Button>
                                  )}
                                </div>
                                {editingOriginsInst === inst._localId ? (
                                  <Textarea
                                    placeholder={"https://ola.vexos.app\n*.vexos.app  (one per line, wildcards ok)"}
                                    value={originDraft}
                                    onChange={(e) => setOriginDraft(e.target.value)}
                                    className="bg-zinc-900 border-zinc-700 text-[11px] font-mono h-20 text-zinc-100"
                                  />
                                ) : (
                                  <div className="flex flex-wrap gap-1">
                                    {(inst.embed_allowed_origins || []).length === 0 ? (
                                      <span className="text-zinc-600 italic">None — widget will be denied. Add at least one origin.</span>
                                    ) : (
                                      (inst.embed_allowed_origins || []).map((o) => (
                                        <Badge key={o} className="bg-zinc-800/80 border-zinc-700 text-zinc-300 font-mono text-[10px]">{o}</Badge>
                                      ))
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <p className="text-[10px] text-zinc-600 italic">
                              No key minted. Generate one to drop the widget on the customer's site.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* One-shot dialog showing the freshly minted embed key. */}
      <Dialog open={!!embedDialog} onOpenChange={(o) => !o && setEmbedDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <Key className="w-4 h-4 text-orange-400" />
              Embed key for {embedDialog?.instance_label || embedDialog?.instance_slug}
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              This is the only time we'll show the full key. Copy it now — if you lose it, you'll have to rotate again (which invalidates the existing snippet on the customer's site).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-xs">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] uppercase text-zinc-500">API key</label>
                <Button size="sm" variant="outline" className="h-6 text-[10px] border-zinc-700" onClick={() => copyToClipboard(embedDialog?.api_key || "", "API key copied")}>
                  <Copy className="w-3 h-3 mr-1" /> Copy
                </Button>
              </div>
              <code className="block bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 font-mono text-[11px] text-orange-300 break-all">
                {embedDialog?.api_key}
              </code>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] uppercase text-zinc-500">HTML snippet (paste before {`</body>`})</label>
                <Button size="sm" variant="outline" className="h-6 text-[10px] border-zinc-700" onClick={() => copyToClipboard(embedDialog?.snippet || "", "Snippet copied")}>
                  <Copy className="w-3 h-3 mr-1" /> Copy
                </Button>
              </div>
              <pre className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 font-mono text-[11px] text-zinc-300 overflow-x-auto whitespace-pre-wrap">{embedDialog?.snippet}</pre>
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Allowed origins ({(embedDialog?.allowed_origins || []).length})</label>
              <div className="flex flex-wrap gap-1 mt-1">
                {(embedDialog?.allowed_origins || []).map((o) => (
                  <Badge key={o} className="bg-zinc-800/80 border-zinc-700 text-zinc-300 font-mono text-[10px]">{o}</Badge>
                ))}
              </div>
              <p className="text-[10px] text-zinc-500 mt-1.5">
                The widget will only respond to requests from these origins. Edit the list from the deployment row after closing this dialog.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-zinc-700" onClick={() => setEmbedDialog(null)}>
              Done — I've copied it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportProductDetailPage;
